import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { getCached, setCached, IDLE_TTL_MS } from "../utils/queryCache";

// Free agency / waiver moves, one league at a time, counted per manager.
//
// move_history (migrations 037, 046, 050) gets one row per resolved roster
// change, whether it came from the instant board before the season opened or
// a Tuesday waiver claim during the season: both insert
// (league_id, member_id, week, team_added, team_dropped) the same way, so a
// row count per member_id is exactly "how many roster moves has this manager
// made," add-only and add-with-drop alike.
//
// The same table also holds event_type = "smack_talk" rows (league chat), so
// the query below must filter to event_type = "move" or it counts chat
// messages as free agency moves.
//
// 5-minute TTL, not the day-long one useLeagueRecords uses for the Hall of
// Fame: that page is a season-long record book nobody expects to update
// mid-visit, but this count sits on the standings table itself, where a
// manager who just made a claim expects the number to catch up on the next
// load rather than the next day.
export function useFreeAgentMoves(leagueId) {
  const [movesByMemberId, setMovesByMemberId] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return undefined;
    let cancelled = false;
    const cacheKey = `faMoves:${leagueId}`;

    const applyRows = (rows) => {
      const counts = new Map();
      (rows || []).forEach((r) => counts.set(r.member_id, (counts.get(r.member_id) || 0) + 1));
      setMovesByMemberId(counts);
    };

    (async () => {
      setLoading(true);

      const cached = getCached(cacheKey);
      if (cached) {
        applyRows(cached);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("move_history")
        .select("member_id")
        .eq("league_id", leagueId)
        .eq("event_type", "move");
      if (cancelled) return;

      if (error) {
        console.error("Error loading free agent moves:", error);
      } else {
        applyRows(data);
        setCached(cacheKey, data, IDLE_TTL_MS);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [leagueId]);

  return { movesByMemberId, loading };
}
